/**
 * Place a `position: fixed` menu next to a trigger.
 *
 * Board columns use `overflow-y: auto`, which also clips overflow-x, so a dropdown
 * absolutely positioned inside a Backlog card is cut off. The menu has to portal to
 * `document.body` and sit in layout pixels — the shell applies `zoom` on `<html>`,
 * so `getBoundingClientRect` is inflated and must be divided by the UI scale before
 * it is used as `top` / `left`.
 */

/** Matches `.menu.tcard-menu` min-width: enough for "Cancel" without the 240px project picker. */
export const CARD_MENU_MIN_WIDTH = 148;

const MENU_VIEWPORT_PAD = 8;
const MENU_GAP = 4;

export interface AnchorBox {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/**
 * Convert a zoom-inflated client coordinate into the layout pixels `position: fixed`
 * uses when `zoom` is set on the document root.
 */
export function layoutPx(clientPx: number, scalePercent: number): number {
  return clientPx / (scalePercent / 100);
}

export function layoutBoxFromClientRect(
  rect: AnchorBox,
  scalePercent: number,
): AnchorBox {
  return {
    top: layoutPx(rect.top, scalePercent),
    left: layoutPx(rect.left, scalePercent),
    right: layoutPx(rect.right, scalePercent),
    bottom: layoutPx(rect.bottom, scalePercent),
  };
}

/**
 * Prefer right-aligning to the trigger (same as the old `right: 0` card menu).
 * Clamp so the panel stays inside the layout viewport, and flip above the
 * trigger when it would run off the bottom.
 */
export function placeAnchoredMenu(input: {
  anchor: AnchorBox;
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  gap?: number;
  pad?: number;
}): { top: number; left: number } {
  const gap = input.gap ?? MENU_GAP;
  const pad = input.pad ?? MENU_VIEWPORT_PAD;
  const maxLeft = Math.max(pad, input.viewportWidth - pad - input.menuWidth);
  let left = input.anchor.right - input.menuWidth;
  if (left < pad) left = pad;
  if (left > maxLeft) left = maxLeft;

  let top = input.anchor.bottom + gap;
  const maxTop = Math.max(pad, input.viewportHeight - pad - input.menuHeight);
  if (top > maxTop) {
    const above = input.anchor.top - gap - input.menuHeight;
    top = above >= pad ? above : maxTop;
  }
  return { top, left };
}
